/**
 * Dynamic Tax & Shipping - Updates tax label and shipping based on country selector
 * Listens for Shopify's localization form changes and updates UI without page reload
 *
 * PRIMARY DATA SOURCE: window.HE_SHIPPING and window.HE_TAX (injected by Liquid)
 * FALLBACK: These hardcoded values are used only if Shopify data is unavailable
 */

(function () {
  'use strict';

  // =============================================================================
  // FALLBACK VALUES - Only used when Shopify data is unavailable
  // =============================================================================

  // Tax rate and shipping cost mapping by country code
  // These should mirror Shopify's Tax & Customs and Shipping Zones settings
  // NOTE: These values are fallbacks only — primary data comes from Shopify via
  //       window.HE_SHIPPING and window.HE_TAX
  var FALLBACK_TAX_RATES = {
    'DE': { rate: 19, name: 'USt.', included: 'inkl.' },
    'FR': { rate: 20, name: 'TVA', included: 'inkl.' },
    'AT': { rate: 20, name: 'USt.', included: 'inkl.' },
    'BE': { rate: 21, name: 'TVA', included: 'inkl.' },
    'NL': { rate: 21, name: 'BTW', included: 'inkl.' },
    'IT': { rate: 22, name: 'IVA', included: 'inkl.' },
    'ES': { rate: 21, name: 'IVA', included: 'inkl.' },
    'PT': { rate: 23, name: 'IVA', included: 'inkl.' },
    'PL': { rate: 23, name: 'VAT', included: 'inkl.' },
    'DK': { rate: 25, name: 'Moms', included: 'inkl.' },
    'SE': { rate: 25, name: 'Moms', included: 'inkl.' },
    'FI': { rate: 24, name: 'ALV', included: 'inkl.' },
    'GR': { rate: 24, name: 'ΦΠΑ', included: 'inkl.' },
    'IE': { rate: 23, name: 'VAT', included: 'inkl.' },
    'HU': { rate: 27, name: 'ÁFA', included: 'inkl.' },
    'CZ': { rate: 21, name: 'DPH', included: 'inkl.' },
    'CH': { rate: 7.7, name: 'MWSt', included: 'inkl.' },
    'GB': { rate: 20, name: 'VAT', included: 'inkl.' }
  };

  // Shipping costs by zone (mirrors Shopify shipping zones)
  var FALLBACK_SHIPPING_COSTS = {
    'DE': { cost: 0, carrier: 'DHL Paket', note: '' },
    'AT': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'FR': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'BE': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'NL': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'IT': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'ES': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'PT': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'PL': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'default_eu': { cost: 11.49, carrier: 'DHL Paket International', note: '' },
    'default': { cost: 19.99, carrier: 'Standard International', note: '' }
  };

  // Delivery times by zone (fallback only - primary data from Shopify)
  var FALLBACK_DELIVERY_TIMES = {
    'DE': { time: '1-3 Werktage', note: 'gilt für Lieferungen innerhalb Deutschlands' },
    'default_eu': { time: '5-8 Werktage', note: 'gilt für Lieferungen nach {country}' },
    'default': { time: '7-14 Werktage', note: 'gilt für Lieferungen nach {country}' }
  };

  var EU_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];

  // =============================================================================
  // HELPER FUNCTIONS
  // =============================================================================

  // Get Shopify shipping zones from window.HE_SHIPPING or use fallback
  function getShopifyShippingZones() {
    if (window.HE_SHIPPING && window.HE_SHIPPING.zones) {
      return window.HE_SHIPPING.zones;
    }
    return null;
  }

  // Get Shopify tax data from window.HE_TAX or use fallback
  function getShopifyTaxData() {
    if (window.HE_TAX && window.HE_TAX.tax_lines) {
      return window.HE_TAX.tax_lines;
    }
    return null;
  }

  // Determine shipping zone for a country code using Shopify data
  function getShippingZone(countryCode) {
    var zones = getShopifyShippingZones();

    // Try to use Shopify shipping zones if available
    if (zones && Array.isArray(zones)) {
      for (var i = 0; i < zones.length; i++) {
        var zone = zones[i];
        if (zone.price_based_shipping_rates) {
          var rates = zone.price_based_shipping_rates || zone.weight_based_shipping_rates || [];
          for (var j = 0; j < rates.length; j++) {
            var rate = rates[j];
            if (rate.qualifies_for_shipping) {
              // Return first available shipping rate for this zone
              return {
                cost: rate.price || rate.cost || 0,
                carrier: rate.name || 'Standard Shipping',
                note: ''
              };
            }
          }
        }
        // Check country-based zones
        if (zone.country_based_zones) {
          var countryZones = zone.country_based_zones || [];
          for (var k = 0; k < countryZones.length; k++) {
            var cz = countryZones[k];
            if (cz.countries) {
              for (var m = 0; m < cz.countries.length; m++) {
                var country = cz.countries[m];
                if (country.iso_code === countryCode) {
                  // Found the country in this zone
                  var rates = cz.price_based_shipping_rates || cz.weight_based_shipping_rates || [];
                  for (var n = 0; n < rates.length; n++) {
                    var rate = rates[n];
                    if (rate.qualifies_for_shipping) {
                      return {
                        cost: rate.price || rate.cost || 0,
                        carrier: rate.name || 'Standard Shipping',
                        note: ''
                      };
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // FALLBACK: Use hardcoded values
    var euCountries = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];
    if (countryCode === 'DE') {
      return FALLBACK_SHIPPING_COSTS['DE'];
    } else if (euCountries.indexOf(countryCode) !== -1) {
      return FALLBACK_SHIPPING_COSTS['default_eu'];
    }
    return FALLBACK_SHIPPING_COSTS['default'];
  }

  // Get tax info for a country using Shopify data
  function getTaxInfo(countryCode) {
    var taxLines = getShopifyTaxData();

    // Try to use Shopify tax_lines if available
    if (taxLines && Array.isArray(taxLines) && taxLines.length > 0) {
      var taxLine = taxLines[0];
      // Extract rate from tax line (Shopify provides the rate as a percentage)
      var rate = taxLine.rate ? taxLine.rate * 100 : 19;
      var title = taxLine.title || 'Tax';

      // Always use German "inkl." prefix since shop language is German
      var name = 'Tax';
      var included = 'inkl.';

      // Map Shopify tax title to local name
      if (title.indexOf('USt') !== -1 || title.indexOf('VAT') !== -1 || title.indexOf('TVA') !== -1 || title.indexOf('BTW') !== -1 || title.indexOf('IVA') !== -1 || title.indexOf('MWSt') !== -1 || title.indexOf('ALV') !== -1 || title.indexOf('Moms') !== -1 || title.indexOf('DPH') !== -1 || title.indexOf('ÁFA') !== -1) {
        name = title;
      }

      return {
        rate: Math.round(rate * 100) / 100,
        name: name,
        included: included
      };
    }

    // FALLBACK: Use hardcoded values (all use "inkl." for German shop)
    return FALLBACK_TAX_RATES[countryCode] || { rate: 20, name: 'Tax', included: 'inkl.' };
  }

  // Get delivery time for a country using Shopify data or fallback
  function getDeliveryTime(countryCode) {
    var zones = getShopifyShippingZones();

    // Try to get delivery time from Shopify shipping zones
    if (zones && Array.isArray(zones)) {
      for (var i = 0; i < zones.length; i++) {
        var zone = zones[i];
        // Check both price_based and weight_based shipping rates
        var rates = zone.price_based_shipping_rates || zone.weight_based_shipping_rates || [];
        for (var j = 0; j < rates.length; j++) {
          var rate = rates[j];
          if (rate.qualifies_for_shipping) {
            // Try to get min/max delivery days from Shopify rate
            var deliveryTime = '';
            if (rate.delivery_min && rate.delivery_max) {
              deliveryTime = rate.delivery_min + '-' + rate.delivery_max + ' Werktage';
            } else if (rate.delivery_days) {
              deliveryTime = rate.delivery_days + ' Werktage';
            }
            if (deliveryTime) {
              return {
                time: deliveryTime,
                note: 'gilt für Lieferungen nach ' + countryCode
              };
            }
          }
        }
        // Also check country_based_zones
        if (zone.country_based_zones) {
          var countryZones = zone.country_based_zones || [];
          for (var k = 0; k < countryZones.length; k++) {
            var cz = countryZones[k];
            if (cz.countries) {
              for (var m = 0; m < cz.countries.length; m++) {
                var country = cz.countries[m];
                if (country.iso_code === countryCode) {
                  var rates = cz.price_based_shipping_rates || cz.weight_based_shipping_rates || [];
                  for (var n = 0; n < rates.length; n++) {
                    var rate = rates[n];
                    if (rate.qualifies_for_shipping) {
                      var deliveryTime = '';
                      if (rate.delivery_min && rate.delivery_max) {
                        deliveryTime = rate.delivery_min + '-' + rate.delivery_max + ' Werktage';
                      } else if (rate.delivery_days) {
                        deliveryTime = rate.delivery_days + ' Werktage';
                      }
                      if (deliveryTime) {
                        return {
                          time: deliveryTime,
                          note: 'gilt für Lieferungen nach ' + countryCode
                        };
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // FALLBACK: Use hardcoded delivery times based on zone
    if (countryCode === 'DE') {
      return FALLBACK_DELIVERY_TIMES['DE'];
    } else if (EU_COUNTRIES.indexOf(countryCode) !== -1) {
      return {
        time: FALLBACK_DELIVERY_TIMES['default_eu'].time,
        note: 'gilt für Lieferungen nach ' + countryCode
      };
    }
    return {
      time: FALLBACK_DELIVERY_TIMES['default'].time,
      note: 'gilt für Lieferungen nach ' + countryCode
    };
  }

  // Format money (Shopify cents to EUR string)
  function formatMoney(cents) {
    // Handle string input (from Shopify JSON)
    if (typeof cents === 'string') {
      cents = parseFloat(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  // =============================================================================
  // UI UPDATE FUNCTIONS
  // =============================================================================

  // Update all tax labels on the page
  function updateTaxLabels(countryCode) {
    var taxInfo = getTaxInfo(countryCode);
    var taxText = taxInfo.included + ' ' + taxInfo.rate + '% ' + taxInfo.name;

    // Update all elements with he-tax-info class
    document.querySelectorAll('.he-tax-info').forEach(function (el) {
      el.textContent = taxText;
      el.setAttribute('data-country', countryCode);
      el.setAttribute('data-tax-rate', taxInfo.rate);
    });
  }

  // Update shipping note and product/footer VAT text
  function updateShippingInfo(countryCode) {
    var shippingInfo = getShippingZone(countryCode);
    var costText = shippingInfo.cost === 0 ? '0,00 €' : formatMoney(shippingInfo.cost * 100);

    // Update shipping note in cart
    var shippingNoteEl = document.querySelector('.he-cart-sidebar__shipping-note');
    if (shippingNoteEl) {
      shippingNoteEl.innerHTML = 'Zzgl. <a href="https://shop.home-elektro.de/pages/versand-und-zahlung" class="he-cart-sidebar__shipping-link">Versandkosten</a> ab <span class="he-cart-shipping-cost">' + costText + '</span> bei Lieferung nach <span class="he-cart-shipping-country">' + countryCode + '</span>';
    }

    // Update product page shipping
    var productVatEl = document.querySelector('.he-product-vat');
    if (productVatEl) {
      var taxInfo = getTaxInfo(countryCode);
      var shippingText = shippingInfo.cost === 0
        ? 'inkl. <a href="https://shop.home-elektro.de/pages/versand-und-zahlung" class="he-product-vat__link">Versand</a>'
        : 'zzgl. ' + formatMoney(shippingInfo.cost * 100) + ' <a href="https://shop.home-elektro.de/pages/versand-und-zahlung" class="he-product-vat__link">Versand</a>';
      productVatEl.innerHTML = taxInfo.included + ' ' + taxInfo.rate + '% ' + taxInfo.name + ', ' + shippingText;
    }

    // Update footer VAT
    var footerVatEl = document.querySelector('.he-footer__vat');
    if (footerVatEl) {
      var taxInfo = getTaxInfo(countryCode);
      footerVatEl.innerHTML = '* Alle Preise ' + taxInfo.included + ' ' + taxInfo.rate + '% ' + taxInfo.name + ', inkl. <a href="/pages/versand-und-zahlung">Versand</a>';
    }
  }

  // =============================================================================
  // CART PRICE SYNC — fetch /cart.js after country change for authoritative prices
  // =============================================================================

  // Fetch Shopify's current cart (prices already adjusted for the active locale)
  // and update every price element on the cart page.
  function fetchCartAndUpdatePrices(countryCode) {
    // Only run when cart price elements are present
    if (!document.getElementById('he-cart-subtotal') && !document.getElementById('he-cart-items')) return;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartPrices(cart, countryCode);
      })
      .catch(function () { /* silently ignore — shipping note already updated */ });
  }

  function updateCartPrices(cart, countryCode) {
    // 1. Update individual line item unit prices and line totals
    var itemsEl = document.getElementById('he-cart-items');
    if (itemsEl) {
      itemsEl.querySelectorAll('[data-line]').forEach(function (row) {
        var idx = parseInt(row.dataset.line, 10) - 1; // data-line is 1-indexed
        var item = cart.items[idx];
        if (!item) return;

        var unitEl = row.querySelector('.he-cart-table__price');
        if (unitEl) unitEl.textContent = formatMoney(item.final_price);

        var lineEl = row.querySelector('.he-cart-table__line-price');
        if (lineEl) lineEl.textContent = formatMoney(item.final_line_price);
      });
    }

    // 2. Zwischensumme — use Shopify's authoritative total_price, not a manual sum
    var subtotalEl = document.getElementById('he-cart-subtotal');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // 3. Shipping cost for the selected country (in cents)
    var shippingInfo = getShippingZone(countryCode);
    var shippingCents = Math.round(shippingInfo.cost * 100);

    // 4. Gesamtbetrag = Shopify subtotal + shipping
    var gesamtbetrag = cart.total_price + shippingCents;
    var totalEl = document.getElementById('he-cart-total');
    if (totalEl) {
      totalEl.textContent = formatMoney(gesamtbetrag);
      totalEl.dataset.subtotal = cart.total_price; // keep data attribute in sync
    }

    // 5. Tax extracted from Gesamtbetrag — VAT applies to shipping too
    var taxEl = document.getElementById('he-cart-tax');
    if (taxEl) {
      var taxInfo = getTaxInfo(countryCode);
      var taxRate = taxInfo.rate / 100;
      var taxAmount = cart.taxes_included
        ? Math.round(gesamtbetrag / (1 + taxRate) * taxRate)
        : Math.round(gesamtbetrag * taxRate);
      taxEl.textContent = formatMoney(taxAmount);
    }
  }

  // Update delivery time on product pages
  function updateDeliveryInfo(countryCode) {
    var deliveryInfo = getDeliveryTime(countryCode);

    // Update delivery time element
    var deliveryTimeEl = document.querySelector('.he-delivery-time');
    if (deliveryTimeEl) {
      deliveryTimeEl.textContent = deliveryInfo.time;
    }

    // Update delivery note element
    var deliveryNoteEl = document.querySelector('.he-delivery-note');
    if (deliveryNoteEl) {
      deliveryNoteEl.textContent = deliveryInfo.note;
    }
  }

  // Get current country code from localization form
  function getCurrentCountryCode() {
    var select = document.querySelector('localization-form [name="country_code"], #HeaderCountryForm [name="country_code"], [data-country-selector]');
    if (select) {
      return select.value || select.dataset.value;
    }
    // Try to get from active element in localization form
    var activeOption = document.querySelector('.localization-form .localization-form__item--active');
    if (activeOption) {
      return activeOption.dataset.value;
    }
    // Fallback: check window.HE_SHIPPING or body data attribute (set by server)
    if (window.HE_SHIPPING && window.HE_SHIPPING.primary_country) {
      return window.HE_SHIPPING.primary_country;
    }
    return document.body.dataset.countryCode || 'DE';
  }

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  // Initialize on page load
  function init() {
    var countryCode = getCurrentCountryCode();
    if (countryCode) {
      updateTaxLabels(countryCode);
      updateShippingInfo(countryCode);
      updateDeliveryInfo(countryCode);
    }
  }

  // Listen for localization form submissions (country changes)
  document.addEventListener('DOMContentLoaded', function () {
    init();

    // Listen for Shopify localization form events
    document.addEventListener('change', function (e) {
      // Check if it's a country selector
      var target = e.target;
      if (target && (target.name === 'country_code' || target.closest('localization-form'))) {
        var form = target.closest('localization-form') || target;
        var countrySelect = form.querySelector('[name="country_code"]');
        if (countrySelect) {
          var countryCode = countrySelect.value;
          updateTaxLabels(countryCode);
          updateShippingInfo(countryCode);
          updateDeliveryInfo(countryCode);
          // Fetch Shopify's recalculated cart prices for the new country.
          // Small delay lets Shopify's own localization-form fetch update the
          // session cookie before we read the cart.
          setTimeout(function () { fetchCartAndUpdatePrices(countryCode); }, 400);
        }
      }
    });

    // Also listen for form submission completion
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form.classList.contains('localization-form')) {
        var countrySelect = form.querySelector('[name="country_code"]');
        if (countrySelect) {
          var countryCode = countrySelect.value;
          updateTaxLabels(countryCode);
          updateShippingInfo(countryCode);
          updateDeliveryInfo(countryCode);
          setTimeout(function () { fetchCartAndUpdatePrices(countryCode); }, 400);
        }
      }
    });

    // Observe DOM changes to catch dynamically added localization forms
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) {
            // Check if this is a country selector
            var select = node.querySelector ? node.querySelector('[name="country_code"]') : null;
            if (select) {
              select.addEventListener('change', function () {
                var countryCode = select.value;
                updateTaxLabels(countryCode);
                updateShippingInfo(countryCode);
                updateDeliveryInfo(countryCode);
                setTimeout(function () { fetchCartAndUpdatePrices(countryCode); }, 400);
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });

  // Also run on load in case DOMContentLoaded already fired
  if (document.readyState !== 'loading') {
    init();
  }
})();